import {
    CheckIcon,
    ChevronDownIcon,
    ClockIcon,
    PhoneArrowUpRightIcon,
    PlusIcon,
} from "@heroicons/react/24/outline";
import {
    ActivityCategory,
    ActivityParentEntityId,
    ActivityTargetView,
    formatPhoneNumber,
    genericLogLabel,
    isPhoneNumberE164,
    PhoneNumberE164,
    PlaybookTouchpointId,
    ReferenceBusinessId,
    ReferenceContactId,
    RegionId,
    TaskTypeId,
    toPhoneNumberE164,
    validEmail,
} from "@sp-crm/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import { PhoneInputE164 } from "components/shared/phone-input-e164";
import { ActivityCategoryIcon } from "components/tasks/activity-chip";
import { ActivityTypeLabel } from "components/tasks/activity-type-name";
import { useActivityTypeOptions } from "components/tasks/activity-type-options";
import {
    LogActivityForm,
    LogActivitySubmission,
} from "components/tasks/log-activity-form";
import { PhoneCall } from "components/telecom/phone-call";
import { SendSmsPanel } from "components/telecom/send-sms-panel";
import { SmsConversation } from "components/telecom/sms-conversation";
import { DatePicker } from "components/ui/date-time-picker/date-picker";
import { InlineBanner } from "components/ui/inline-banner";
import { Input } from "components/ui/input";
import { Panel } from "components/ui/panel/panel";
import { PanelType } from "components/ui/panel/panel-type";
import {
    PrimaryButton,
    primaryClasses,
    primaryCompactClasses,
} from "components/ui/primary-button";
import {
    SecondaryButton,
    SecondaryCompactButton,
    secondaryCompactClasses,
} from "components/ui/secondary-button";
import { Select } from "components/ui/select";
import { TextArea } from "components/ui/textarea";
import {
    attendeeCandidateOptions,
    attendeeCandidatesQueryOptions,
} from "components/visits/attendee-candidates";
import { VisitParent } from "components/visits/types";
import { VisitEditor } from "components/visits/visit-editor";
import { addMonths, addWeeks } from "date-fns";
import {
    usePatchReferenceBusinessMutation,
    usePatchReferenceContactMutation,
} from "generated/graphql";
import {
    Menu,
    MenuButton,
    MenuHeading,
    MenuItem,
    MenuItems,
    MenuSection,
} from "lib/headlessui/components/menu/menu";
import React, { useEffect, useState } from "react";
import { useQueryClient as useLegacyQueryClient } from "react-query";
import { Link } from "react-router-dom";
import { useRegionId } from "store/selectors/hooks";
import { handleEvent } from "util/user-events";
import {
    activityChoicesForCategory,
    activityTypesForMarketingPlan,
    EmailActionState,
    LoggedActivityChoice,
    loggedActivityChoices,
    LoggedActivityGroup,
    loggedActivityGroups,
    logLabelForCategory,
    OrgAgendaContactOption,
    toTouchpointLogParent,
} from "./marketing-plan-agenda";
import { useAgendaRefresh } from "./use-agenda-refresh";

export const contactPath = (contactId: string): string =>
    `/references/contacts/show/${contactId}`;

export const orgPath = (businessId: string): string =>
    `/references/organizations/show/${businessId}`;

const provisionPath = "/settings/account/phone";

const iconClass = "h-3.5 w-3.5 flex-shrink-0";

const menuItemClasses =
    "flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-700 hover:bg-brand-100 hover:text-brand-900";

const menuItemsClasses =
    "z-1300 max-h-80 w-56 overflow-y-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-gray-200 " +
    "focus:outline-none [--anchor-gap:0.25rem] [--anchor-padding:0.5rem]";

const menuHeadingClasses =
    "px-4 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-gray-400";

const ActivityActionButton: React.FC<{
    label: string;
    category: ActivityCategory;
    targets: ActivityTargetView[];
    onChoose: (activityTypeId: TaskTypeId | null) => void;
}> = ({ label, category, targets, onChoose }) => {
    const activityTypes = useActivityTypeOptions();
    const choices = activityChoicesForCategory(
        targets,
        category,
        activityTypesForMarketingPlan(activityTypes, targets),
    );
    const icon = <ActivityCategoryIcon category={category} className={iconClass} />;
    if (choices.length < 2) {
        return (
            <SecondaryCompactButton
                className="items-center gap-1"
                onClick={() => onChoose(choices[0]?.taskTypeId ?? null)}>
                {icon}
                {label}
            </SecondaryCompactButton>
        );
    }
    return (
        <Menu as="div" className="inline-block text-left">
            <MenuButton className={`items-center gap-1 ${secondaryCompactClasses}`}>
                {icon}
                {label}
                <ChevronDownIcon className="h-3 w-3 flex-shrink-0" />
            </MenuButton>
            <MenuItems anchor="bottom start" className={menuItemsClasses}>
                {choices.map(choice => (
                    <MenuItem key={choice.key}>
                        <button
                            type="button"
                            className={menuItemClasses}
                            onClick={() => onChoose(choice.taskTypeId)}>
                            {choice.label}
                        </button>
                    </MenuItem>
                ))}
            </MenuItems>
        </Menu>
    );
};

const ButtonLink: React.FC<{
    to: string;
    variant: "primary" | "secondary";
    children: React.ReactNode;
}> = ({ to, variant, children }) => (
    <Link
        to={to}
        className={`items-center gap-1 ${
            variant === "primary" ? primaryCompactClasses : secondaryCompactClasses
        }`}>
        {children}
    </Link>
);

const AddContactPhonePanel: React.FC<{
    parent: VisitParent;
    isOpen: boolean;
    onClose: () => void;
}> = ({ parent, isOpen, onClose }) => {
    const invalidateAgenda = useAgendaRefresh();
    const legacyQueryClient = useLegacyQueryClient();
    const patchContact = usePatchReferenceContactMutation();
    const patchBusiness = usePatchReferenceBusinessMutation();
    const [phone, setPhone] = useState<PhoneNumberE164 | null>(null);
    const committedInvalid = !!phone && !isPhoneNumberE164(phone);
    const isSaving = patchContact.isLoading || patchBusiness.isLoading;

    const save = async () => {
        if (!phone || !isPhoneNumberE164(phone)) {
            return;
        }
        if (parent.kind === "contact") {
            await patchContact.mutateAsync({
                id: parent.id,
                contactEntity: { cellPhone: phone },
            });
            await legacyQueryClient.invalidateQueries("getReferenceContact");
        } else {
            await patchBusiness.mutateAsync({
                id: parent.id,
                businessContactEntity: { phone1: phone },
            });
            await legacyQueryClient.invalidateQueries("getReferenceBusiness");
        }
        handleEvent("marketing-plan-add-phone");
        setPhone(null);
        onClose();
        await invalidateAgenda();
    };

    return (
        <Panel
            isOpen={isOpen}
            onDismiss={onClose}
            type={PanelType.medium}
            headerText="Add phone number"
            hasCloseButton={true}>
            <div className="space-y-4">
                <p className="text-sm text-gray-700">
                    This phone number will be saved to {parent.name}&apos;s record.
                </p>
                <PhoneInputE164
                    label="Phone number"
                    initial={phone}
                    onCommit={setPhone}
                    showButtons={false}
                />
                {committedInvalid ? (
                    <p className="text-sm text-red-600">
                        Enter a valid 10-digit phone number.
                    </p>
                ) : null}
                <div className="flex justify-end gap-2">
                    <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                    <PrimaryButton onClick={save} disabled={isSaving || committedInvalid}>
                        Save
                    </PrimaryButton>
                </div>
            </div>
        </Panel>
    );
};

const AddContactEmailPanel: React.FC<{
    parent: VisitParent;
    isOpen: boolean;
    onClose: () => void;
}> = ({ parent, isOpen, onClose }) => {
    const invalidateAgenda = useAgendaRefresh();
    const legacyQueryClient = useLegacyQueryClient();
    const patchContact = usePatchReferenceContactMutation();
    const patchBusiness = usePatchReferenceBusinessMutation();
    const [email, setEmail] = useState("");
    const isSaving = patchContact.isLoading || patchBusiness.isLoading;

    const save = async () => {
        const trimmed = email.trim();
        if (!validEmail(trimmed)) {
            return;
        }
        if (parent.kind === "contact") {
            await patchContact.mutateAsync({
                id: parent.id,
                contactEntity: { email1: trimmed },
            });
            await legacyQueryClient.invalidateQueries("getReferenceContact");
        } else {
            await patchBusiness.mutateAsync({
                id: parent.id,
                email: trimmed,
            });
            await legacyQueryClient.invalidateQueries("getReferenceBusiness");
        }
        handleEvent("marketing-plan-add-email");
        setEmail("");
        onClose();
        await invalidateAgenda();
    };

    return (
        <Panel
            isOpen={isOpen}
            onDismiss={onClose}
            type={PanelType.medium}
            headerText="Add email"
            hasCloseButton={true}>
            <div className="space-y-4">
                <p className="text-sm text-gray-700">
                    This email address will be saved to {parent.name}&apos;s record.
                </p>
                <Input
                    label="Email address"
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                />
                <div className="flex justify-end gap-2">
                    <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                    <PrimaryButton
                        onClick={save}
                        disabled={isSaving || !validEmail(email.trim())}>
                        Save
                    </PrimaryButton>
                </div>
            </div>
        </Panel>
    );
};

const AgendaAddContactEmail: React.FC<{ parent: VisitParent }> = ({ parent }) => {
    const [isOpen, setIsOpen] = useState(false);
    return (
        <>
            <SecondaryCompactButton
                className="items-center gap-1"
                onClick={() => setIsOpen(true)}>
                <PlusIcon className={iconClass} />
                Add email
            </SecondaryCompactButton>
            <AddContactEmailPanel
                parent={parent}
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
            />
        </>
    );
};

export const AgendaEmailAction: React.FC<{
    state: EmailActionState;
    parent: VisitParent;
    targets: ActivityTargetView[];
    onSendEmail: (activityTypeId: TaskTypeId | null) => void;
}> = ({ state, parent, targets, onSendEmail }) => {
    if (state === "email-opted-out") {
        return (
            <span className="self-center text-xs italic text-gray-400">
                Opted out of email
            </span>
        );
    }
    if (state === "add-email") {
        return <AgendaAddContactEmail parent={parent} />;
    }
    return (
        <ActivityActionButton
            label="Send email"
            category="email"
            targets={targets}
            onChoose={onSendEmail}
        />
    );
};

export const AgendaAddContactPhone: React.FC<{
    parent: VisitParent;
}> = ({ parent }) => {
    const [isOpen, setIsOpen] = useState(false);
    return (
        <>
            <SecondaryCompactButton
                className="items-center gap-1"
                onClick={() => setIsOpen(true)}>
                <PlusIcon className={iconClass} />
                Add phone number
            </SecondaryCompactButton>
            <AddContactPhonePanel
                parent={parent}
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
            />
        </>
    );
};

export const AgendaProvisionPhone: React.FC<unknown> = () => (
    <ButtonLink to={provisionPath} variant="secondary">
        <PhoneArrowUpRightIcon className={iconClass} />
        Set up calling
    </ButtonLink>
);

interface ActivityPanelChoice {
    activityTypeId: TaskTypeId | null;
}

interface ActivityActionPanelState {
    activeChoice: ActivityPanelChoice | null;
    choose: (activityTypeId: TaskTypeId | null) => void;
    dismiss: () => void;
}

const useActivityActionPanel = (): ActivityActionPanelState => {
    const invalidateAgenda = useAgendaRefresh();
    const [pendingChoice, setPendingChoice] = useState<ActivityPanelChoice | null>(null);
    return {
        activeChoice: pendingChoice,
        choose: activityTypeId => setPendingChoice({ activityTypeId }),
        dismiss: () => {
            setPendingChoice(null);
            invalidateAgenda();
        },
    };
};

export const AgendaCallAction: React.FC<{
    phone: PhoneNumberE164;
    entityId: ActivityParentEntityId;
    targets: ActivityTargetView[];
}> = ({ phone, entityId, targets }) => {
    const { activeChoice: activeCall, choose, dismiss } = useActivityActionPanel();
    return (
        <>
            <ActivityActionButton
                label="Call"
                category="call"
                targets={targets}
                onChoose={choose}
            />
            <Panel
                isOpen={activeCall !== null}
                onDismiss={dismiss}
                type={PanelType.medium}
                headerText="Phone Call"
                hasCloseButton={true}>
                {activeCall ? (
                    <div className="space-y-3">
                        <ActivityTypeLabel activityTypeId={activeCall.activityTypeId} />
                        <PhoneCall
                            phoneNumber={phone}
                            entityId={entityId}
                            activityTypeId={activeCall.activityTypeId}
                        />
                    </div>
                ) : null}
            </Panel>
        </>
    );
};

export const AgendaTextAction: React.FC<{
    phone: PhoneNumberE164;
    targets: ActivityTargetView[];
    entityId: ActivityParentEntityId | null;
}> = ({ phone, targets, entityId }) => {
    const { activeChoice: activeText, choose, dismiss } = useActivityActionPanel();
    return (
        <>
            <ActivityActionButton
                label="Text"
                category="sms"
                targets={targets}
                onChoose={choose}
            />
            <SendSmsPanel
                isOpen={activeText !== null}
                onDismiss={dismiss}
                recipient={phone}
                activityTypeId={activeText?.activityTypeId ?? null}
                entityId={entityId}
            />
        </>
    );
};

interface OrgPhoneOption {
    value: PhoneNumberE164;
    label: string;
    entityId: ActivityParentEntityId;
}

const orgPhoneOptions = (
    orgPhone: PhoneNumberE164 | null,
    orgName: string,
    businessId: ReferenceBusinessId,
    contacts: OrgAgendaContactOption[],
): OrgPhoneOption[] => [
    ...(orgPhone
        ? [
              {
                  value: orgPhone,
                  label: `${orgName} — ${formatPhoneNumber(orgPhone)}`,
                  entityId: businessId,
              },
          ]
        : []),
    ...contacts
        .filter(contact => isPhoneNumberE164(contact.phone))
        .map(contact => ({
            value: toPhoneNumberE164(contact.phone),
            label: `${contact.name} — ${formatPhoneNumber(contact.phone)}`,
            entityId: contact.id as ReferenceContactId,
        })),
];

const useOrgPhoneSelection = (
    orgPhone: PhoneNumberE164 | null,
    orgName: string,
    businessId: ReferenceBusinessId,
    contacts: OrgAgendaContactOption[],
) => {
    const options = orgPhoneOptions(orgPhone, orgName, businessId, contacts);
    const [selectedOption, setSelectedOption] = useState<OrgPhoneOption | null>(
        options[0] ?? null,
    );
    const setSelectedPhone = (phone: PhoneNumberE164) => {
        setSelectedOption(options.find(option => option.value === phone) ?? null);
    };
    const reset = () => setSelectedOption(options[0] ?? null);
    return {
        options,
        selectedOption,
        selectedPhone: selectedOption?.value ?? null,
        setSelectedPhone,
        reset,
    };
};

const OrgPhoneOptionPicker: React.FC<{
    businessId: ReferenceBusinessId;
    orgPhone: PhoneNumberE164 | null;
    options: OrgPhoneOption[];
    selectedPhone: PhoneNumberE164 | null;
    onChange: (phone: PhoneNumberE164) => void;
}> = ({ businessId, orgPhone, options, selectedPhone, onChange }) => (
    <div className="space-y-3">
        {!orgPhone && options.length > 0 ? (
            <InlineBanner type="warning">
                This organization doesn&apos;t have a phone number.{" "}
                <Link to={orgPath(businessId)} className="underline">
                    Edit the organization&apos;s phone number
                </Link>
                , or choose a contact below.
            </InlineBanner>
        ) : null}
        {!orgPhone && options.length === 0 ? (
            <InlineBanner type="warning">
                There is no phone number for this organization or any of its contacts.{" "}
                <Link to={orgPath(businessId)} className="underline">
                    Click here to edit and add one.
                </Link>
            </InlineBanner>
        ) : null}
        {options.length > 0 ? (
            <Select
                label="Number"
                value={selectedPhone ?? ""}
                onChange={e => onChange(e.target.value as PhoneNumberE164)}>
                {options.map(option => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </Select>
        ) : null}
    </div>
);

export const AgendaOrgCallAction: React.FC<{
    businessId: ReferenceBusinessId;
    orgPhone: PhoneNumberE164 | null;
    orgName: string;
    contacts: OrgAgendaContactOption[];
    targets: ActivityTargetView[];
}> = ({ businessId, orgPhone, orgName, contacts, targets }) => {
    const { activeChoice: activeCall, choose, dismiss } = useActivityActionPanel();
    const { options, selectedOption, selectedPhone, setSelectedPhone, reset } =
        useOrgPhoneSelection(orgPhone, orgName, businessId, contacts);
    return (
        <>
            <ActivityActionButton
                label="Call"
                category="call"
                targets={targets}
                onChoose={activityTypeId => {
                    reset();
                    choose(activityTypeId);
                }}
            />
            <Panel
                isOpen={activeCall !== null}
                onDismiss={dismiss}
                type={PanelType.medium}
                headerText="Phone Call"
                hasCloseButton={true}>
                {activeCall ? (
                    <div className="space-y-3">
                        <ActivityTypeLabel activityTypeId={activeCall.activityTypeId} />
                        <OrgPhoneOptionPicker
                            businessId={businessId}
                            orgPhone={orgPhone}
                            options={options}
                            selectedPhone={selectedPhone}
                            onChange={setSelectedPhone}
                        />
                        {selectedOption ? (
                            <PhoneCall
                                phoneNumber={selectedOption.value}
                                entityId={selectedOption.entityId}
                                activityTypeId={activeCall.activityTypeId}
                            />
                        ) : null}
                    </div>
                ) : null}
            </Panel>
        </>
    );
};

export const AgendaOrgTextAction: React.FC<{
    businessId: ReferenceBusinessId;
    orgPhone: PhoneNumberE164 | null;
    orgName: string;
    contacts: OrgAgendaContactOption[];
    targets: ActivityTargetView[];
}> = ({ businessId, orgPhone, orgName, contacts, targets }) => {
    const { activeChoice: activeText, choose, dismiss } = useActivityActionPanel();
    const { options, selectedOption, selectedPhone, setSelectedPhone, reset } =
        useOrgPhoneSelection(orgPhone, orgName, businessId, contacts);
    return (
        <>
            <ActivityActionButton
                label="Text"
                category="sms"
                targets={targets}
                onChoose={activityTypeId => {
                    reset();
                    choose(activityTypeId);
                }}
            />
            <Panel
                isOpen={activeText !== null}
                onDismiss={dismiss}
                type={PanelType.large}
                headerText={
                    selectedPhone
                        ? `Your Conversation with ${formatPhoneNumber(selectedPhone)}`
                        : "Text Message"
                }
                hasCloseButton={true}>
                {activeText ? (
                    <div className="space-y-3">
                        <ActivityTypeLabel activityTypeId={activeText.activityTypeId} />
                        <OrgPhoneOptionPicker
                            businessId={businessId}
                            orgPhone={orgPhone}
                            options={options}
                            selectedPhone={selectedPhone}
                            onChange={setSelectedPhone}
                        />
                        {selectedOption ? (
                            <SmsConversation
                                recipient={selectedOption.value}
                                activityTypeId={activeText.activityTypeId}
                                entityId={selectedOption.entityId}
                                onClose={dismiss}
                            />
                        ) : null}
                    </div>
                ) : null}
            </Panel>
        </>
    );
};

export const AgendaVisitAction: React.FC<{
    parent: VisitParent;
    targets: ActivityTargetView[];
}> = ({ parent, targets }) => {
    const invalidateAgenda = useAgendaRefresh();
    const [activeVisit, setActiveVisit] = useState<{
        activityTypeId: TaskTypeId | null;
    } | null>(null);
    return (
        <>
            <ActivityActionButton
                label="Schedule visit"
                category="visit"
                targets={targets}
                onChoose={activityTypeId => setActiveVisit({ activityTypeId })}
            />
            {activeVisit ? (
                <VisitEditor
                    mode="new"
                    parent={parent}
                    activityTypeId={activeVisit.activityTypeId}
                    onSaved={invalidateAgenda}
                    onClose={() => setActiveVisit(null)}
                />
            ) : null}
        </>
    );
};

const LogActivityMenu: React.FC<{
    groups: LoggedActivityGroup[];
    onChoose: (choice: LoggedActivityChoice) => void;
}> = ({ groups, onChoose }) => {
    const choices = loggedActivityChoices(groups);
    if (choices.length === 0) {
        return null;
    }
    if (choices.length === 1) {
        return (
            <SecondaryCompactButton
                className="items-center gap-1"
                onClick={() => onChoose(choices[0])}>
                <CheckIcon className={iconClass} />
                {genericLogLabel}
            </SecondaryCompactButton>
        );
    }
    return (
        <Menu as="div" className="inline-block text-left">
            <MenuButton className={`items-center gap-1 ${secondaryCompactClasses}`}>
                <CheckIcon className={iconClass} />
                {genericLogLabel}
                <ChevronDownIcon className="h-3 w-3 flex-shrink-0" />
            </MenuButton>
            <MenuItems anchor="bottom start" className={menuItemsClasses}>
                {groups.map(group => (
                    <MenuSection key={group.category}>
                        {groups.length > 1 ? (
                            <MenuHeading className={menuHeadingClasses}>
                                {group.label}
                            </MenuHeading>
                        ) : null}
                        {group.choices.map(choice => (
                            <MenuItem key={choice.key}>
                                <button
                                    type="button"
                                    className={menuItemClasses}
                                    onClick={() => onChoose(choice)}>
                                    {choice.label}
                                </button>
                            </MenuItem>
                        ))}
                    </MenuSection>
                ))}
            </MenuItems>
        </Menu>
    );
};

export const LogActivityPanel: React.FC<{
    parent: VisitParent;
    choice: LoggedActivityChoice | null;
    isOpen: boolean;
    onClose: () => void;
}> = ({ parent, choice, isOpen, onClose }) => {
    const regionId = useRegionId();
    const trpcClient = trpc.useTRPC();
    const invalidateAgenda = useAgendaRefresh();
    const [involvedContactIds, setInvolvedContactIds] = useState<ReferenceContactId[]>(
        [],
    );
    useEffect(() => {
        setInvolvedContactIds([]);
    }, [choice?.key]);

    const businessId = parent.kind === "organization" ? parent.id : null;
    const showsAttendeePicker = !!choice && !!businessId;
    const attendeeCandidatesQuery = useQuery(
        attendeeCandidatesQueryOptions(trpcClient, businessId),
    );
    const attendeeOptions = attendeeCandidateOptions(attendeeCandidatesQuery.data);

    const logMutation = useMutation(
        trpcClient.marketingPlaybook.logTouchpoint.mutationOptions(),
    );

    const save = (submission: LogActivitySubmission) => {
        if (!choice) {
            return;
        }
        logMutation.mutate(
            {
                regionId: regionId as RegionId,
                parent: toTouchpointLogParent(parent),
                target: choice.key,
                note: submission.note,
                occurredAt: submission.happenedAt.toISOString(),
                involvedContactIds: showsAttendeePicker ? involvedContactIds : [],
            },
            {
                onSuccess: () => {
                    handleEvent("marketing-plan-mark-done", {
                        category: choice.category,
                        targetKind: choice.taskTypeId ? "taskType" : "category",
                    });
                    handleEvent("activity-logged", { source: "my-agenda" });
                    setInvolvedContactIds([]);
                    onClose();
                    invalidateAgenda();
                },
            },
        );
    };

    return (
        <Panel
            isOpen={isOpen}
            onDismiss={onClose}
            type={PanelType.medium}
            headerText={logLabelForCategory(choice?.category ?? null)}
            hasCloseButton={true}>
            {choice && isOpen ? (
                <LogActivityForm
                    typeLabel={choice.label}
                    category={choice.category}
                    submitLabel={logLabelForCategory(choice.category)}
                    isSubmitting={logMutation.isPending}
                    attendeePicker={
                        showsAttendeePicker
                            ? {
                                  options: attendeeOptions,
                                  value: involvedContactIds,
                                  onChange: setInvolvedContactIds,
                              }
                            : null
                    }
                    onSubmit={save}
                    onCancel={onClose}
                />
            ) : null}
        </Panel>
    );
};

type SnoozePreset = "skipOnce" | "1week" | "1month" | "3months" | "custom";

const presetSnoozeUntil = (preset: SnoozePreset, customDate: string): Date | null => {
    switch (preset) {
        case "1week":
            return addWeeks(new Date(), 1);
        case "1month":
            return addMonths(new Date(), 1);
        case "3months":
            return addMonths(new Date(), 3);
        case "custom":
            return customDate ? new Date(customDate) : null;
        case "skipOnce":
            return null;
    }
};

const SnoozePanel: React.FC<{
    parent: VisitParent;
    touchpointId: string | null;
    isOpen: boolean;
    onClose: () => void;
}> = ({ parent, touchpointId, isOpen, onClose }) => {
    const regionId = useRegionId();
    const trpcClient = trpc.useTRPC();
    const invalidateAgenda = useAgendaRefresh();
    const [reason, setReason] = useState("");
    const [customDate, setCustomDate] = useState("");
    const [preset, setPreset] = useState<SnoozePreset | null>(null);

    const snoozeMutation = useMutation({
        ...trpcClient.marketingPlaybook.snooze.mutationOptions(),
        onSuccess: () => {
            handleEvent("marketing-plan-snooze", {
                scope: touchpointId ? "touchpoint" : parent.kind,
            });
            onClose();
            invalidateAgenda();
        },
    });

    const resolveIntent = ():
        | { kind: "skipOnce" }
        | { kind: "until"; snoozeUntil: string }
        | null => {
        if (preset === null) {
            return null;
        }
        if (preset === "skipOnce") {
            return { kind: "skipOnce" };
        }
        const date = presetSnoozeUntil(preset, customDate);
        return date ? { kind: "until", snoozeUntil: date.toISOString() } : null;
    };

    const intent = resolveIntent();

    const submit = () => {
        if (!intent) {
            return;
        }
        snoozeMutation.mutate({
            regionId: regionId as RegionId,
            parent: toTouchpointLogParent(parent),
            touchpointId: touchpointId ? (touchpointId as PlaybookTouchpointId) : null,
            intent,
            reason: reason.trim() ? reason.trim() : null,
        });
    };

    const presetOptions: { key: SnoozePreset; label: string }[] = [];
    if (touchpointId) {
        presetOptions.push({ key: "skipOnce", label: "Skip once" });
    }
    presetOptions.push({ key: "1week", label: "1 week" });
    presetOptions.push({ key: "1month", label: "1 month" });
    presetOptions.push({ key: "3months", label: "3 months" });

    return (
        <Panel
            isOpen={isOpen}
            onDismiss={onClose}
            type={PanelType.medium}
            headerText={touchpointId ? "Snooze this activity" : "Snooze all outreach"}
            hasCloseButton={true}>
            <div className="space-y-4">
                <TextArea
                    label="Reason (optional)"
                    value={reason}
                    placeholder="e.g. out of office until the fall"
                    onChange={e => setReason(e.target.value)}
                />
                <div className="space-y-2">
                    <span className="text-sm font-medium text-gray-700">Snooze for</span>
                    <div className="flex flex-wrap gap-2">
                        {presetOptions.map(option =>
                            preset === option.key ? (
                                <PrimaryButton
                                    key={option.key}
                                    onClick={() => setPreset(option.key)}>
                                    {option.label}
                                </PrimaryButton>
                            ) : (
                                <SecondaryButton
                                    key={option.key}
                                    onClick={() => setPreset(option.key)}>
                                    {option.label}
                                </SecondaryButton>
                            ),
                        )}
                    </div>
                </div>
                <div className="border-t border-gray-100 pt-3">
                    <DatePicker
                        label="Or until a specific date"
                        value={customDate || null}
                        onChange={v => {
                            setCustomDate(v ?? "");
                            setPreset(v ? "custom" : null);
                        }}
                        disabled={false}
                        required={false}
                        textSize="base"
                        onOpenChange={() => undefined}
                        minValue={null}
                        maxValue={null}
                        visibleYears={20}
                    />
                </div>
                <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
                    <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                    <PrimaryButton
                        disabled={!intent || snoozeMutation.isPending}
                        onClick={submit}>
                        Snooze
                    </PrimaryButton>
                </div>
            </div>
        </Panel>
    );
};

export const AgendaTouchpointActions: React.FC<{
    parent: VisitParent;
    touchpointId: string;
    targets: ActivityTargetView[];
}> = ({ parent, touchpointId, targets }) => {
    const invalidateAgenda = useAgendaRefresh();
    const [logChoice, setLogChoice] = useState<LoggedActivityChoice | null>(null);
    const [logPanelOpen, setLogPanelOpen] = useState(false);
    const [visitEditorOpen, setVisitEditorOpen] = useState(false);
    const [snoozeOpen, setSnoozeOpen] = useState(false);

    const activityTypes = useActivityTypeOptions();
    const groups = loggedActivityGroups(
        targets,
        activityTypesForMarketingPlan(activityTypes, targets),
    );

    const chooseActivity = (choice: LoggedActivityChoice) => {
        setLogChoice(choice);
        if (choice.category === "visit") {
            setVisitEditorOpen(true);
        } else {
            setLogPanelOpen(true);
        }
    };

    const onVisitRecorded = () => {
        handleEvent("marketing-plan-mark-done", {
            category: "visit",
            targetKind: logChoice?.taskTypeId ? "taskType" : "category",
        });
        setVisitEditorOpen(false);
        invalidateAgenda();
    };

    return (
        <>
            <LogActivityMenu groups={groups} onChoose={chooseActivity} />
            <SecondaryCompactButton
                className="items-center"
                aria-label="Snooze"
                title="Snooze"
                onClick={() => setSnoozeOpen(true)}>
                <ClockIcon className="h-4 w-4" />
            </SecondaryCompactButton>
            <LogActivityPanel
                parent={parent}
                choice={logChoice}
                isOpen={logPanelOpen}
                onClose={() => setLogPanelOpen(false)}
            />
            {visitEditorOpen ? (
                <VisitEditor
                    mode="complete"
                    parent={parent}
                    activityTypeId={logChoice?.taskTypeId ?? null}
                    onSaved={onVisitRecorded}
                    onClose={() => setVisitEditorOpen(false)}
                />
            ) : null}
            <SnoozePanel
                parent={parent}
                touchpointId={touchpointId}
                isOpen={snoozeOpen}
                onClose={() => setSnoozeOpen(false)}
            />
        </>
    );
};

export const AgendaProvisionBanner: React.FC<unknown> = () => (
    <InlineBanner type="info">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <span>
                Set up a phone number to call and text your referral sources right from My
                Agenda.
            </span>
            <Link
                to={provisionPath}
                className={`items-center whitespace-nowrap ${primaryClasses}`}>
                <PhoneArrowUpRightIcon className="mr-1.5 h-4 w-4 flex-shrink-0" />
                Set up phone number
            </Link>
        </div>
    </InlineBanner>
);
